import { callMethod } from '@ipfabric/utils/fp/function';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { option } from 'fp-ts';

import type { EdgeId, NodeData, NodeId } from '@ipfabric/utils';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import type { SearchResult } from 'components/Graph/GraphState';
import { getNodeIconSize } from 'components/Graph/helpers/getNodeIconSize';
import { constFalse, not, pipe } from 'fp-ts/lib/function';
import { Ellipse, Graphics } from 'pixi.js';
import {
  ANIMATION_BACKWARD,
  type AnimationDirection,
  createBouncingAnimationController,
} from 'utils/pixi/animation/BouncingAnimationController';
import { easeInCubic } from 'utils/pixi/animation/easing';
import { suspendAnimateOnLowFps } from 'utils/pixi/animation/suspendAnimateOnLowFps';
import { SEARCH_HIGHLIGHT_COLOR } from '../colors';
import { ZINDEX_MATCH_HIGHLIGHT_AREA } from '../zIndexes';

const EDGE_HIGHLIGHT_AREA_SIZE = 55;
const HIGHLIGHT_ANIMATION_TIME = 200;

const drawHiglightArea = (nodeData: NodeData): Graphics => {
  const hlArea = new Graphics();
  hlArea.position.set(nodeData.position.x, nodeData.position.y);
  hlArea.name = 'node-highlight-area';
  hlArea.zIndex = ZINDEX_MATCH_HIGHLIGHT_AREA;

  const nodeSize = getNodeIconSize(nodeData);
  const radiusX = nodeSize.width / 2 + EDGE_HIGHLIGHT_AREA_SIZE;
  const radiusY = nodeSize.height / 2 + EDGE_HIGHLIGHT_AREA_SIZE;

  const areaShape = new Ellipse(0, 0, radiusX, radiusY);

  hlArea.clear();
  hlArea.beginFill(SEARCH_HIGHLIGHT_COLOR, 0.6);
  hlArea.drawShape(areaShape);
  hlArea.hitArea = areaShape;
  hlArea.endFill();
  return hlArea;
};

// @TODO: Extract search logic from plugin
export const nodeHighlight: GraphRendererPlugin = (pluginInterface) => {
  let highlightArea: Graphics | null = null;

  const destroyHighlightArea = () => {
    highlightArea?.destroy();
    highlightArea = null;
  };

  const applyAnimationPhase = (
    phase: number,
    direction: AnimationDirection,
  ) => {
    if (direction === ANIMATION_BACKWARD && phase === 0) {
      destroyHighlightArea();
    }
    const hlAreaScale = 0.4 + phase * 0.6;
    highlightArea?.scale.set(hlAreaScale, hlAreaScale);
  };

  const suspendableAnimate = suspendAnimateOnLowFps(
    pluginInterface.animate,
    pluginInterface.requestRender,
  );

  const animationController = createBouncingAnimationController(
    suspendableAnimate,
    HIGHLIGHT_ANIMATION_TIME,
    applyAnimationPhase,
    easeInCubic,
  );

  const addHighlight = (nodeExactMatch: NodeData) => {
    pipe(
      animationController.getRunningAnimation(),
      option.map(callMethod('cancel')),
    );

    highlightArea = drawHiglightArea(nodeExactMatch);
    pluginInterface.graphLayer.addChild(highlightArea);
    animationController.forward();
  };

  const updateMutedDisplayObjects = (
    isNodeMuted: (nodeId: NodeId) => boolean,
    isEdgeMuted: (edgeId: EdgeId) => boolean,
  ) => {
    pluginInterface.nodeDisplayObjects.forEach((node, nodeId) => {
      node.setMutedBySearch(isNodeMuted(nodeId));
    });
    pluginInterface.edgeDisplayObjects.forEach((edge, edgeId) => {
      edge.setMutedBySearch(isEdgeMuted(edgeId));
    });
  };

  const updateSearchResult = ({
    edgeIds,
    nodeExactMatch,
    nodeIds,
  }: SearchResult) => {
    destroyHighlightArea();
    if (nodeExactMatch) {
      addHighlight(nodeExactMatch);
    }

    updateMutedDisplayObjects(
      not(plainSet.isInSet(nodeIds)),
      not(plainSet.isInSet(edgeIds)),
    );

    pluginInterface.requestRender();
  };

  const clearAnimation = () => {
    updateMutedDisplayObjects(constFalse, constFalse);
    pluginInterface.requestRender();
    animationController.backward();
  };

  pluginInterface.events.addListener('searchResultChange', () => {
    const result = pluginInterface.getSearchResult();

    if (result === null) {
      clearAnimation();
    } else {
      updateSearchResult(result);
    }
  });

  // Hide highlight area on drag start
  pluginInterface.events.addListener('nodeDragStart', ({ nodeIds }) => {
    const result = pluginInterface.getSearchResult();

    if (!result?.nodeExactMatch) {
      return;
    }

    if (nodeIds.includes(result.nodeExactMatch.id)) {
      animationController.backward();
    }
  });

  // Update highlight on node change
  pluginInterface.events.addListener('nodeChange', ({ bounds, nodeData }) => {
    const result = pluginInterface.getSearchResult();

    if (!result?.nodeExactMatch || nodeData.id !== result?.nodeExactMatch?.id) {
      return;
    }

    // Node has been hidden
    if (option.isNone(bounds)) {
      animationController.backward();
      return;
    }

    // Node position changes (drag/alignment/distribution) or is unhiden
    // We have to destroy highlight area to cover alignment/distribution where
    // it is still shown
    destroyHighlightArea();
    addHighlight(nodeData);
  });

  pluginInterface.bindGraphCommandEventListener(
    'nodeHighlight',
    ({ nodeId }) => {
      if (nodeId === null) {
        clearAnimation();
      } else {
        const nodeData = pluginInterface.getGraphData().nodes[nodeId];
        if (nodeData !== undefined) {
          destroyHighlightArea();
          addHighlight(nodeData);
        }
      }
    },
  );
};
